import { z } from 'zod';
import { BUILDING_TYPES, normalizePhone } from './crm';
import { type AbsenceStatus, type AbsenceType, type ApprovalDecision, LEAVE_KIND_KEYS, type LeaveKind } from './planning';
import { fieldFleetReportSchema, fieldOdometerSchema } from './fleet';
import './schemas'; // polskie komunikaty zod

/**
 * Aplikacja pracownika — stałe kategorie (dolny pasek). Praca bieżąca nie miesza się ze sprawami
 * kadrowymi: każda kategoria grupuje swoje moduły. Kategoria bez dostępnego modułu jest wyszarzona.
 */
export const MOBILE_CATEGORIES = {
  praca: { name: 'Praca', description: 'Zadania, protokoły, zgłoszenia nowych spraw' },
  urlopy: { name: 'Kadry', description: 'Wydarzenia firmowe; urlopy: plan, wnioski, rozliczenie; czas pracy' },
  wynagrodzenia: { name: 'Płace', description: 'Paski płac i rozliczenia wynagrodzeń' },
  zasoby: { name: 'Zasoby', description: 'Wyposażenie na stanie i samochód służbowy' },
} as const;
export type MobileCategoryKey = keyof typeof MOBILE_CATEGORIES;

/**
 * Moduły — osobne części aplikacji (ekrany, dane, wysyłka) w swojej kategorii, widoczne, gdy pracownik
 * ma wymagane uprawnienie. Serwer wylicza listę przy każdej synchronizacji, więc zmiana roli w biurze
 * zmienia aplikację bez aktualizacji.
 *
 * `ready: false` — moduł zaplanowany: jest w rejestrze, żeby układ i uprawnienia były od początku wspólne,
 * ale nie trafia do telefonu, dopóki nie zostanie zbudowany.
 */
export const MOBILE_MODULES = {
  zadania: { category: 'praca', name: 'Moje zadania', description: 'Zadania, czynności, zdjęcia, protokoły', permission: 'zadania.podglad', ready: true },
  sprawy: { category: 'praca', name: 'Zgłoszenia spraw', description: 'Nowy klient z terenu (np. sąsiad)', permission: 'sprawy.tworzenie', ready: true },
  wydarzenia: { category: 'urlopy', name: 'Wydarzenia firmowe', description: 'Spotkania i szkolenia — potwierdzenie obecności', permission: null, ready: true },
  urlopy: { category: 'urlopy', name: 'Urlopy', description: 'Wnioski urlopowe i ich akceptacja', permission: 'nieobecnosci.wniosek', ready: true },
  rcp: { category: 'urlopy', name: 'Czas pracy', description: 'Rejestracja czasu pracy', permission: null, ready: false },
  place: { category: 'wynagrodzenia', name: 'Paski płac', description: 'Wynagrodzenia', permission: null, ready: false },
  wyposazenie: { category: 'zasoby', name: 'Wyposażenie', description: 'Narzędzia i sprzęt na stanie', permission: null, ready: false },
  // moduł Flota (docs/19): widoczny tylko przy włączonym module i przypisanym aucie (serwer filtruje w profilu)
  // docs/20: wycena, termin (koordynator), oferta, podpis klienta i płatność u klienta — tryb oferta → zlecenie bez umowy
  sprzedaz: { category: 'praca', name: 'Sprzedaż w terenie', description: 'Wycena, termin, oferta i podpis u klienta', permission: 'oferty.edycja', ready: true },
  // kalendarz ekip (podgląd obłożenia i wolne terminy) — koordynator; serwer zostawia moduł tylko przy przydziale w kolejce koordynatora
  kalendarz: { category: 'praca', name: 'Kalendarz ekip', description: 'Obłożenie ekip i wolne terminy', permission: 'planowanie.edycja', ready: true },
  // docs/22: miesiąc i lista dni pracownika, własne wpisy („Moje zadanie”) jak w CRM — wymaga sieci
  moj_kalendarz: { category: 'praca', name: 'Mój kalendarz', description: 'Mój miesiąc i własne wpisy (np. wizyta u klienta)', permission: 'modul.moj_kalendarz', ready: true },
  samochody: { category: 'zasoby', name: 'Samochody', description: 'Przypisane auto, zgłoszenia problemów, przebieg', permission: null, ready: true },
} as const satisfies Record<string, { category: MobileCategoryKey; name: string; description: string; permission: string | null; ready: boolean }>;
export type MobileModuleKey = keyof typeof MOBILE_MODULES;

/** Moduły dostępne dla pracownika (gotowe i z uprawnieniem). */
export function mobileModules(has: (permission: string) => boolean): MobileModuleKey[] {
  return (Object.entries(MOBILE_MODULES) as [MobileModuleKey, (typeof MOBILE_MODULES)[MobileModuleKey]][])
    .filter(([, m]) => m.ready && (m.permission === null || has(m.permission)))
    .map(([k]) => k);
}

/** Profil pracownika na telefonie — moduły i słowniki potrzebne formularzom (odświeżany przy synchronizacji). */
export interface MobileProfile {
  modules: MobileModuleKey[];
  /** aktywne branże — wybór zakresu w zgłoszeniu sprawy */
  trades: { code: string; name: string }[];
  /** moduł „Urlopy”: może składać własne wnioski (bez tego — tylko decyzje jako osoba zatwierdzająca) */
  leaveRequests?: boolean;
  /** moduł „Sprzedaż w terenie”: branże, w których użytkownik prowadzi oferty (przydział w kolejce handlowca), i czy widzi terminy (koordynator tej branży) */
  sales?: { trades: { code: string; name: string; slots: boolean }[]; slots: boolean };
  /** moduł „Kalendarz ekip”: branże, w których użytkownik jest koordynatorem */
  crews?: { trades: { code: string; name: string }[] };
  /** produkty magazynowe do zgłaszania zapotrzebowania (bez cen) — tylko z uprawnieniem zapotrzebowania.zglaszanie */
  materialsCatalog?: { id: string; name: string; code: string; unit: string }[];
}

// ── zgłoszenia z telefonu (telefon → serwer) ────────────────────────────
//
// Zgłoszenie to jednorazowy dokument wysyłany z kolejki na telefonie (także po odzyskaniu sieci):
// identyfikator nadaje telefon, więc ponowna wysyłka niczego nie dubluje. Załączniki (zdjęcia)
// idą wcześniej zwykłą wysyłką plików w kawałkach; zgłoszenie wskazuje je identyfikatorami.
// Kolejne moduły (wniosek urlopowy, tankowanie…) dodają tu swój rodzaj.

const uuid = z.string().uuid();
const optStr = (max: number) => z.string().trim().max(max).default('');
const postalCode = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d{2}-\d{3}$/.test(v), 'Kod pocztowy w formacie 00-000')
  .default('');

export const fieldCaseSchema = z
  .object({
    firstName: optStr(80),
    lastName: z.string().trim().min(2, 'Podaj nazwisko').max(80),
    phone: z
      .string()
      .trim()
      .max(30)
      .refine((v) => v === '' || normalizePhone(v) !== null, 'Numer telefonu musi mieć co najmniej 9 cyfr')
      .default(''),
    email: z.union([z.literal(''), z.string().trim().toLowerCase().email('Niepoprawny adres e-mail')]).default(''),
    street: optStr(120),
    postalCode,
    city: z.string().trim().min(2, 'Podaj miejscowość').max(80),
    buildingType: z.enum(Object.keys(BUILDING_TYPES) as [keyof typeof BUILDING_TYPES, ...(keyof typeof BUILDING_TYPES)[]]).nullable().default(null),
    tradeCodes: z.array(z.string().trim().min(2).max(4)).min(1, 'Wybierz co najmniej jedną branżę').max(10),
    description: optStr(4000),
    /** zadanie, przy którym padło zgłoszenie (np. sąsiad klienta) — trafia do opisu sprawy */
    fromTaskId: uuid.nullable().default(null),
    photoIds: z.array(uuid).max(20).default([]),
  })
  .superRefine((v, ctx) => {
    if (!v.phone && !v.email) ctx.addIssue({ code: 'custom', path: ['phone'], message: 'Podaj telefon albo e-mail klienta' });
  });
export type FieldCaseInput = z.infer<typeof fieldCaseSchema>;

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/,'Data w formacie RRRR-MM-DD');

/** Wniosek o nieobecność z telefonu (moduł „Urlopy”) — trafia do drabinki akceptacji pracownika. */
export const fieldLeaveSchema = z
  .object({
    absenceType: z.enum(['urlop', 'l4', 'szkolenie', 'inne']).default('urlop'),
    leaveKind: z.enum(LEAVE_KIND_KEYS).nullable().default('wypoczynkowy'),
    dateFrom: day,
    dateTo: day,
    note: optStr(1000),
  })
  .refine((v) => v.dateTo >= v.dateFrom, { message: 'Data „do” nie może być wcześniejsza niż „od”', path: ['dateTo'] });
export type FieldLeaveInput = z.infer<typeof fieldLeaveSchema>;

/** Decyzja zatwierdzającego z telefonu; odmowa wymaga powodu. */
export const fieldLeaveDecisionSchema = z
  .object({ entryId: uuid, decision: z.enum(['zatwierdzona', 'odrzucona']), note: optStr(500) })
  .refine((v) => v.decision !== 'odrzucona' || v.note.length >= 3, { message: 'Podaj powód odmowy', path: ['note'] });

export const SUBMISSION_KINDS = {
  'sprawa.nowa': 'Nowa sprawa',
  obecnosc: 'Potwierdzenie obecności',
  'urlop.wniosek': 'Wniosek urlopowy',
  'urlop.wycofanie': 'Wycofanie wniosku',
  'urlop.decyzja': 'Decyzja o wniosku',
  'flota.zgloszenie': 'Zgłoszenie problemu z samochodem',
  'flota.przebieg': 'Przebieg samochodu',
} as const;
export type SubmissionKind = keyof typeof SUBMISSION_KINDS;

export const submissionSchema = z.discriminatedUnion('kind', [
  z.object({ id: uuid, kind: z.literal('sprawa.nowa'), deviceTime: z.string().datetime(), data: fieldCaseSchema }),
  z.object({ id: uuid, kind: z.literal('obecnosc'), deviceTime: z.string().datetime(), data: z.object({ entryId: uuid, present: z.boolean() }) }),
  z.object({ id: uuid, kind: z.literal('urlop.wniosek'), deviceTime: z.string().datetime(), data: fieldLeaveSchema }),
  z.object({ id: uuid, kind: z.literal('urlop.wycofanie'), deviceTime: z.string().datetime(), data: z.object({ entryId: uuid }) }),
  z.object({ id: uuid, kind: z.literal('urlop.decyzja'), deviceTime: z.string().datetime(), data: fieldLeaveDecisionSchema }),
  z.object({ id: uuid, kind: z.literal('flota.zgloszenie'), deviceTime: z.string().datetime(), data: fieldFleetReportSchema }),
  z.object({ id: uuid, kind: z.literal('flota.przebieg'), deviceTime: z.string().datetime(), data: fieldOdometerSchema }),
]);

/** Wniosek o nieobecność na telefonie (Kadry → Urlopy): mój albo czekający na moją decyzję. */
export interface MobileLeaveDto {
  id: string;
  personName: string;
  title: string;
  absenceType: AbsenceType;
  leaveKind: LeaveKind | null;
  dateFrom: string;
  dateTo: string;
  note: string;
  /** wycofany — anulowany przez pracownika albo biuro */
  status: AbsenceStatus | 'wycofany';
  createdAt: string;
  /** powód odmowy / uwaga przy decyzji końcowej */
  decisionNote: string;
  decidedByName: string | null;
  steps: { position: number; approverName: string; decision: ApprovalDecision | null; decidedByName: string | null; note: string; decidedAt: string | null }[];
  /** wniosek w toku: kto decyduje teraz */
  waitingFor: string[];
  can: { cancel: boolean; decide: boolean };
}

/** Wydarzenie firmowe na telefonie (Kadry → Wydarzenia): najbliższe tygodnie, z moją odpowiedzią. */
export interface MobileEventDto {
  id: string;
  title: string;
  dateFrom: string;
  dateTo: string;
  timeFrom: string | null;
  timeTo: string | null;
  note: string;
  attendanceRequired: boolean;
  /** moja odpowiedź (z telefonu albo wpisana przez biuro); null — brak */
  mine: { present: boolean; source: import('./schemas-planning').AttendanceSource; at: string } | null;
  createdByName: string | null;
}
export type Submission = z.infer<typeof submissionSchema>;
export type SubmissionInput = z.input<typeof submissionSchema>;

/** Wynik zgłoszenia: przyjęte (z odnośnikiem do utworzonego dokumentu) albo odrzucone z powodem. */
export interface SubmissionResult {
  id: string;
  status: 'przyjete' | 'odrzucone';
  message: string;
  /** np. numer utworzonej sprawy */
  ref: { id: string; number: string } | null;
}
